import { useEffect, useMemo, useState } from 'react'
import { useAttributes } from '../attributes/useAttributes'
import { designerAPI } from '../designer/api'
import { pipelineRulesAPI, type BaseCategory } from '../pipeline-rules/api'
import { sellersAPI } from '../sellers/api'
import { normalizeMarketplace, themeAttributeOptions } from './referenceValues'

export interface FunnelSellerOption {
  id: string
  /** Сокращённое имя продавца — для показа человеку («ВБ КСА», «Сайт»). */
  cabinet: string
  /**
   * Машинный код кабинета из справочника продавцов (`ksa_wb`, `site_pt`).
   *
   * Именно он обязан попадать в правило воронки: резолвер рендера сравнивает
   * `content_funnel_rules.cabinet` с кодом кабинета ТОЧНО
   * (`content_funnels/resolver.py`), и сокращённое имя там не совпадёт ни с чем.
   * Пусто у старых записей справочника — тогда остаётся прежнее поведение.
   */
  cabinetCode?: string
  name: string
  marketplace: string
}

function unwrapSellers(response: any): any[] {
  const value = response?.data?.data ?? response?.data ?? response
  return Array.isArray(value) ? value : []
}

export function useContentFunnelOptions(active = true) {
  const { values: attributes, loading: attributesLoading } = useAttributes()
  const [categories, setCategories] = useState<BaseCategory[]>([])
  const [sellers, setSellers] = useState<FunnelSellerOption[]>([])
  const [themes, setThemes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!active) return
    let alive = true
    setLoading(true)
    void Promise.allSettled([
      pipelineRulesAPI.getCategories().then((items) => alive && setCategories(items)),
      sellersAPI.getSellers().then((response) => {
        if (!alive) return
        setSellers(
          unwrapSellers(response)
            .filter((seller) => seller.active ?? seller.is_active ?? true)
            .map((seller) => ({
              id: String(seller.seller_id ?? seller.id ?? ''),
              cabinet: String(
                seller.seller_short_name ??
                  seller.sellerShortName ??
                  seller.short_name ??
                  seller.id ??
                  '',
              ),
              cabinetCode: String(seller.cabinet_code ?? seller.cabinetCode ?? ''),
              name: String(seller.seller_name ?? seller.sellerName ?? seller.name ?? 'Кабинет'),
              marketplace: normalizeMarketplace(seller.marketplace ?? seller.mp),
            }))
            .filter((seller) => seller.id && seller.cabinet),
        )
      }),
      designerAPI.getThemes().then((result) => {
        if (!alive) return
        setThemes(themeAttributeOptions(result.themes || []))
      }),
    ]).finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [active])

  return useMemo(
    () => ({
      attributes,
      categories,
      sellers,
      themes,
      loading: loading || attributesLoading,
    }),
    [attributes, attributesLoading, categories, loading, sellers, themes],
  )
}
